import { Link } from "react-router-dom";
import { Heart, Plus, Timer } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { inr, pctOff, buildLabel } from "../lib/api";
import { Stars } from "./Stars";

export const ProductCard = ({ product: p }) => {
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const saved = inWishlist(p.slug);
  return (
    <article data-testid={`product-card-${p.slug}`} className="group flex flex-col">
      <div className="img-zoom relative aspect-[4/5] overflow-hidden rounded-md bg-jute/40">
        <Link to={`/product/${p.slug}`} data-testid={`product-card-link-${p.slug}`}>
          <img src={p.images[0]} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
        </Link>
        <div className="absolute left-3 top-3 flex gap-1.5">
          {p.badge && <span className="rounded-full bg-walnut px-2.5 py-1 text-[11px] font-semibold text-linen">{p.badge}</span>}
          {pctOff(p) > 0 && <span className="rounded-full bg-sheesham px-2.5 py-1 text-[11px] font-semibold text-linen">{pctOff(p)}% off</span>}
        </div>
        <button
          data-testid={`wishlist-toggle-${p.slug}`}
          onClick={() => toggleWishlist(p)}
          aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-linen/90 text-walnut transition-transform hover:scale-110"
        >
          <Heart className={`h-4 w-4 ${saved ? "fill-sheesham text-sheesham" : ""}`} />
        </button>
        <button
          data-testid={`quick-add-${p.slug}`}
          onClick={() => addToCart(p)}
          className="absolute bottom-3 right-3 flex translate-y-2 items-center gap-1 rounded-full bg-sheesham px-4 py-2 text-xs font-semibold text-linen opacity-0 transition-[opacity,transform] duration-300 group-hover:translate-y-0 group-hover:opacity-100 max-md:translate-y-0 max-md:opacity-100"
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
      <div className="mt-4 flex flex-1 flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Timer className="h-3.5 w-3.5" />{buildLabel(p.build_minutes)}</span>
          {p.review_count > 0 && (
            <span className="inline-flex items-center gap-1"><Stars value={p.rating} size={12} />({p.review_count})</span>
          )}
        </div>
        {p.variants?.length > 0 && (
          <div data-testid={`card-swatches-${p.slug}`} className="flex items-center gap-1.5">
            {p.variants.map((v) => (
              <Link key={v.key} to={`/product/${p.slug}?finish=${v.key}`} title={v.name} aria-label={v.name} style={{ backgroundColor: v.swatch }} className="h-4 w-4 rounded-full ring-1 ring-border ring-offset-1 ring-offset-background" />
            ))}
            <span className="text-xs text-muted-foreground">{p.variants.length} finishes</span>
          </div>
        )}
        <Link to={`/product/${p.slug}`} className="font-display text-xl leading-snug hover:text-sheesham dark:hover:text-brass">{p.name}</Link>
        <p className="line-clamp-2 text-sm text-muted-foreground">{p.tagline}</p>
        <div className="mt-auto flex items-baseline gap-2 pt-1">
          <span data-testid={`product-price-${p.slug}`} className="price text-xl">{inr(p.price)}</span>
          {p.mrp > p.price && <span className="mrp text-sm">{inr(p.mrp)}</span>}
          {p.gst_exclusive && <span className="text-xs text-muted-foreground">excl. tax</span>}
        </div>
      </div>
    </article>
  );
};
